import { NoteType } from "../../types/credit-debit-note.type";
import { roundHalfUp } from "../../utils/money";
import { VAT_RATE_PERCENT } from "../../utils/vat";

/**
 * Live PREVIEW of a credit / debit note's totals while it is keyed in — the same rule the
 * server applies when it stores the note, rounded the same way (half away from zero, 2 dp),
 * so the figure on screen is the one the saved note comes back with. The server stays
 * authoritative: the detail page shows the stored totals, never these.
 *
 * Lines are priced the way the original invoice was:
 * - `IN` (VAT-inclusive): the lines total carries the VAT, so the difference is the lines
 *   total with the VAT taken out, and the lines total is the grand total.
 * - anything else (`EX`, `N/A`, unset): the lines total is the difference, VAT is added on top.
 *
 * The correct value moves the original invoice's ex-VAT value by the difference: down for a
 * credit note, up for a debit note. With no original value yet there is no correct value.
 */

export interface NoteLineInput {
  qty: number;
  unit_price: number;
  discount_amount: number;
}

export interface NoteTotals {
  /** Σ line totals. */
  linesTotal: number;
  /** ผลต่าง — the ex-VAT amount the note moves the invoice by. */
  difference: number;
  /** ภาษีมูลค่าเพิ่ม 7% */
  vatAmount: number;
  /** จำนวนเงินรวมทั้งสิ้น */
  grandTotal: number;
  /** มูลค่าสินค้าที่ถูกต้อง; null until the original value is known. */
  correctValue: number | null;
}

export const noteLineTotal = (line: NoteLineInput): number =>
  roundHalfUp(line.qty * line.unit_price - line.discount_amount);

export function computeNoteTotals(
  lines: NoteLineInput[],
  taxType: string | null | undefined,
  noteType: NoteType,
  originalValue: number | null,
): NoteTotals {
  const linesTotal = roundHalfUp(
    lines.reduce((sum, line) => sum + noteLineTotal(line), 0),
  );

  let difference: number;
  let vatAmount: number;
  let grandTotal: number;
  if (taxType === "IN") {
    difference = roundHalfUp((linesTotal * 100) / (100 + VAT_RATE_PERCENT));
    vatAmount = roundHalfUp(linesTotal - difference);
    grandTotal = linesTotal;
  } else {
    difference = linesTotal;
    vatAmount = roundHalfUp((difference * VAT_RATE_PERCENT) / 100);
    grandTotal = roundHalfUp(difference + vatAmount);
  }

  let correctValue: number | null = null;
  if (originalValue !== null) {
    correctValue = roundHalfUp(
      noteType === "CREDIT" ? originalValue - difference : originalValue + difference,
    );
  }

  return { linesTotal, difference, vatAmount, grandTotal, correctValue };
}
